import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Check, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { EmptyState } from '@/components/EmptyState';
import { getTasks, createTask, updateTask, deleteTask, getClients } from '@/lib/api';
import { toast } from 'sonner';

const STATUS_OPTIONS = [
  { value: 'offen', label: 'Offen', icon: AlertCircle, color: 'bg-amber-50 text-amber-700' },
  { value: 'in_bearbeitung', label: 'In Bearbeitung', icon: Clock, color: 'bg-sky-50 text-sky-700' },
  { value: 'erledigt', label: 'Erledigt', icon: Check, color: 'bg-emerald-50 text-emerald-700' },
];

const PRIORITIES = [
  { value: 'low', label: 'Niedrig', color: 'bg-slate-100 text-slate-600' },
  { value: 'medium', label: 'Mittel', color: 'bg-sky-50 text-sky-700' },
  { value: 'high', label: 'Hoch', color: 'bg-amber-50 text-amber-700' },
  { value: 'urgent', label: 'Dringend', color: 'bg-red-50 text-red-700' },
];

const fadeIn = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.3 } } };

export default function AdminTasksPage() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ client_id: '', title: '', description: '', assignee: '', priority: 'medium', status: 'offen', due_date: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [tRes, clRes] = await Promise.all([getTasks(), getClients()]);
      setTasks(tRes.data);
      setClients(clRes.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  };

  const handleSave = async () => {
    try {
      if (editingTask) {
        await updateTask(editingTask.id, form);
        toast.success('Task aktualisiert');
      } else {
        await createTask(form);
        toast.success('Task erstellt');
      }
      setDialogOpen(false); setEditingTask(null); loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await updateTask(task.id, { status: newStatus });
      toast.success(`Status geändert: ${STATUS_OPTIONS.find(s => s.value === newStatus)?.label}`);
      loadData();
    } catch (err) { toast.error('Fehler'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Task löschen?')) return;
    try { await deleteTask(id); toast.success('Gelöscht'); loadData(); } catch (err) { toast.error('Fehler'); }
  };

  const getClientName = (id) => clients.find(c => c.id === id)?.name || '';

  const filtered = tasks.filter(t => filterStatus === 'all' || t.status === filterStatus);

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.05 } } }}>
      <PageHeader title="Tasks" description={`${tasks.length} Tasks | ${tasks.filter(t => t.status !== 'erledigt').length} offen`}>
        <Button onClick={() => { setEditingTask(null); setForm({ client_id: '', title: '', description: '', assignee: '', priority: 'medium', status: 'offen', due_date: '' }); setDialogOpen(true); }} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="task-create-button">
          <Plus className="w-4 h-4 mr-2" /> Neuer Task
        </Button>
      </PageHeader>

      <motion.div variants={fadeIn} className="flex gap-2 mb-4">
        <Button variant={filterStatus === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('all')}>Alle</Button>
        {STATUS_OPTIONS.map(s => (
          <Button key={s.value} variant={filterStatus === s.value ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus(s.value)}>
            {s.label}
          </Button>
        ))}
      </motion.div>

      {filtered.length === 0 && !loading ? (
        <EmptyState title="Keine Tasks" description="Erstellen Sie Ihren ersten Task." actionLabel="Task erstellen" action={() => setDialogOpen(true)} />
      ) : (
        <motion.div variants={fadeIn} className="space-y-3">
          {filtered.map((task, i) => {
            const statusInfo = STATUS_OPTIONS.find(s => s.value === task.status) || STATUS_OPTIONS[0];
            const priorityInfo = PRIORITIES.find(p => p.value === task.priority) || PRIORITIES[1];
            return (
              <div key={task.id || i} className="kpi-card-hover rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-4" data-testid={`task-item-${task.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className={`text-sm font-medium ${task.status === 'erledigt' ? 'text-slate-400 line-through' : 'text-slate-900'}`}>{task.title}</h4>
                    <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${priorityInfo.color}`}>{priorityInfo.label}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500">
                    {task.assignee && <span>{task.assignee}</span>}
                    {task.due_date && <span>Fällig: {task.due_date}</span>}
                    {task.client_id && <span>{getClientName(task.client_id)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Select value={task.status} onValueChange={v => handleStatusChange(task, v)}>
                    <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button variant="ghost" size="sm" onClick={() => { setEditingTask(task); setForm({ client_id: task.client_id, title: task.title, description: task.description, assignee: task.assignee, priority: task.priority, status: task.status, due_date: task.due_date }); setDialogOpen(true); }}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(task.id)} className="text-red-500">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            );
          })}
        </motion.div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editingTask ? 'Task bearbeiten' : 'Neuer Task'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Titel *</label><Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Task Titel" data-testid="task-form-title" /></div>
            <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Beschreibung</label><Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Beschreibung..." /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Zugewiesen an</label><Input value={form.assignee} onChange={e => setForm({...form, assignee: e.target.value})} placeholder="Name" /></div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Fällig am</label><Input type="date" value={form.due_date} onChange={e => setForm({...form, due_date: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Priorität</label>
                <Select value={form.priority} onValueChange={v => setForm({...form, priority: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><label className="text-sm font-medium text-slate-700 mb-1.5 block">Kunde (optional)</label>
                <Select value={form.client_id || 'none'} onValueChange={v => setForm({...form, client_id: v === 'none' ? '' : v})}><SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger><SelectContent><SelectItem value="none">Kein Kunde</SelectItem>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleSave} className="btn-lift bg-slate-900 text-white hover:bg-slate-800" data-testid="task-form-save">{editingTask ? 'Aktualisieren' : 'Erstellen'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
